import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { getMsalInstance } from "../msalConfig";
import {
  getAdminAccess,
  getSupervisorAccess,
  getSemanticAdminAccess,
  getUserEmail,
  setAdminAccess,
  setSupervisorAccess,
  setSemanticAdminAccess,
  ensureSessionLoginAt,
  isSessionExpired,
} from "../utils/userUtils";
import { performAppLogout } from "../utils/authLogout";
import { adminService } from "../services/adminService";
import { supervisorService } from "../services/supervisorService";
import { semanticService } from "../services/semanticService";

export default function ProtectedRoute({
  children,
  requireAdminAccess = false,
  requireSupervisorAccess = false,
  requireSemanticAdminAccess = false,
}) {
  // // TEMPORARY BYPASS - Remove this in production
  // return children;

  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAllowed, setIsAllowed] = useState(!requireAdminAccess && !requireSupervisorAccess && !requireSemanticAdminAccess);
  const msalInstance = getMsalInstance();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await msalInstance.initialize();
        const account = msalInstance.getActiveAccount();
        const allAccounts = msalInstance.getAllAccounts();

        const isAuthed = Boolean(account || allAccounts.length > 0);
        setIsAuthenticated(isAuthed);
        if (!isAuthed) return;

        ensureSessionLoginAt();
        if (isSessionExpired()) {
          await performAppLogout();
          return;
        }

        const email = getUserEmail() || account?.username || allAccounts?.[0]?.username || "";
        const requiresAccess = requireAdminAccess || requireSupervisorAccess || requireSemanticAdminAccess;
        if (!email) {
          setAdminAccess(false);
          setSupervisorAccess(false);
          setSemanticAdminAccess(false);
          setIsAllowed(!requiresAccess);
          return;
        }

        const checkAdminAccess = async () => {
          const response = await adminService.checkAccess(email);
          return Boolean(response && response.access_granted === true);
        };

        const checkSupervisorAccess = async () => {
          const response = await supervisorService.checkAccess(email);
          return Boolean(response && response.access_granted === true);
        };

        const checkSemanticAdminAccess = async () => {
          const response = await semanticService.checkAccess(email);
          return Boolean(response && response.access_granted === true);
        };

        if (requireAdminAccess) {
          try {
            const granted = await checkAdminAccess();
            setAdminAccess(granted);
            setIsAllowed(granted);
          } catch (error) {
            setIsAllowed(getAdminAccess());
          }
          return;
        }

        if (requireSupervisorAccess) {
          try {
            const granted = await checkSupervisorAccess();
            setSupervisorAccess(granted);
            setIsAllowed(granted);
          } catch (error) {
            setIsAllowed(getSupervisorAccess());
          }
          return;
        }

        if (requireSemanticAdminAccess) {
          try {
            const granted = await checkSemanticAdminAccess();
            setSemanticAdminAccess(granted);
            setIsAllowed(granted);
          } catch (error) {
            setIsAllowed(getSemanticAdminAccess());
          }
          return;
        }

        setIsAllowed(true);
      } catch (error) {
        if (requireAdminAccess) setAdminAccess(false);
        if (requireSupervisorAccess) setSupervisorAccess(false);
        if (requireSemanticAdminAccess) setSemanticAdminAccess(false);
        if (requireAdminAccess || requireSupervisorAccess || requireSemanticAdminAccess) setIsAllowed(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [msalInstance, requireAdminAccess, requireSupervisorAccess, requireSemanticAdminAccess]);

  if (isChecking) {
    const message = requireSemanticAdminAccess
      ? "Checking semantic admin access..."
      : requireSupervisorAccess
        ? "Checking supervisor access..."
        : requireAdminAccess
          ? "Checking admin access..."
          : "Authenticating...";

    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: "48px",
            height: "48px",
            border: "4px solid #E5E7EB",
            borderTopColor: "#B90005",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }}></div>
          <p style={{ color: "#6B7280", fontSize: "14px" }}>{message}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if ((requireAdminAccess || requireSupervisorAccess || requireSemanticAdminAccess) && !isAllowed) {
    const label = requireSupervisorAccess
      ? "dashboard"
      : requireSemanticAdminAccess
        ? "semantic workspace"
        : "admin panel";
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "24px", background: "#f9fafb" }}>
        <div style={{ maxWidth: "520px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "14px", padding: "24px", boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)", textAlign: "center" }}>
          <h2 style={{ margin: "0 0 8px", color: "#111827" }}>Access check failed</h2>
          <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.5 }}>
            You are signed in, but this account is not currently approved for the {label}, or the access-check API failed.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
